'use client';

import { useEffect, useRef } from 'react';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import HeroSection from '@/sections/HeroSection';
import TextBenefitsSection from '@/sections/TextBenefitsSection';
import SummarySection from '@/sections/SummarySection';
import ImageSwiperSection from '@/sections/ImageSwiperSection';
import MissionSection from '@/sections/MissionSection';
import ValuesSection from '@/sections/ValuesSection';
import TeamSection from '@/sections/TeamSection';
import PartnersSection from '@/sections/PartnersSection';
import LocationSection from '@/sections/LocationSection';
import NavbarContainer from '@/components/client/NavbarContainer';
import FooterWrapper from '@/components/ui/FooterWrapper';
import { useLoading } from '@/contexts/LoadingContext';
import useAspectRatio from '@/hooks/useAspectRatio';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import {
  AboutImageSectionContent,
  BranchSectionContent,
  BrandInfoSectionContent,
  CTASectionContent,
  LanguageType,
  PageData,
  TeamMemberSectionContent,
} from '@/lib/types';

import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
import { setLanguage } from '@/lib/language';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface AboutPageClientProps {
  pageData: PageData | null;
  initialLanguage: LanguageType;
}

export default function AboutPageClient({ pageData, initialLanguage }: AboutPageClientProps) {
  const language: LanguageType = initialLanguage;
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();
  const isLg = useIsLg();
  const animatedNavigate = useAnimatedNavigate();

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const shadowRef = useRef<HTMLDivElement | null>(null);
  const summaryCenterRef = useRef<number | null>(null);
  const imageSwiperPositionRef = useRef<number | null>(null);
  const teamPositionRef = useRef<number | null>(null);
  const locationPositionRef = useRef<number | null>(null);
  const valuesCardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    document.body.style.backgroundColor = '#FFF6E6';
    document.body.classList.add('will-change-bg');

    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);

  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    if (aspectRatio !== 0) {
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);

  const { contextSafe } = useGSAP({});

  // TODO: the color transition might get broken on resizes. check check check

  // Create a safe animation function that can be called anytime
  const animateBg = contextSafe(() => {
    // Kill only the color transition ScrollTriggers
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.id?.toString().startsWith('color-transition-')) {
        trigger.kill();
      }
    });

    if (summaryCenterRef.current && imageSwiperPositionRef.current && teamPositionRef.current && locationPositionRef.current) {

      const sections: {
        startY: number;
        fromColor: string;
        toColor: string;
      }[] = [];
      sections.push({
        startY: summaryCenterRef.current, // Start transition slightly before center
        fromColor: '#FFF6E6', // TODO: use variables
        toColor: '#806B51', // TODO: use variables
      });
      sections.push({
        startY: imageSwiperPositionRef.current, // Start transition slightly before center
        fromColor: '#806B51', // TODO: use variables
        toColor: '#FFF6E6',
      });
      sections.push({
        startY: teamPositionRef.current,
        fromColor: '#FFF6E6',
        toColor: '#F7E8D7',
      });
      sections.push({
        startY: locationPositionRef.current,
        fromColor: '#F7E8D7',
        toColor: '#FFF6E6',
      });

      sections.forEach((section, index) => {
        ScrollTrigger.create({
          // start: `top+=${section.startY} center`,
          start:  index === 0 && isLg ? `top+=${section.startY} bottom` : `top+=${section.startY} center`,
          // end: `top+=${section.endY} center`,
          // markers: true,
          id: `color-transition-${index}`,
          onEnter: () => {
            // Create a timeline to run animations in parallel
            const tl = gsap.timeline();

            // Animate document body background
            tl.to(document.body, {
              backgroundColor: section.toColor,
              duration: 1,
              ease: "power2.inOut"
            }, 0); // Start at time 0

            // If this is the ValuesSection transition (index 1), also animate card backgrounds in parallel
            const validCards = valuesCardsRef.current.filter(Boolean);
            if (validCards.length > 0) {
              tl.to(validCards, {
                backgroundColor: section.toColor,
                duration: 1,
                ease: "power2.inOut"
              }, 0); // Start at time 0 (same time as body animation)
            }
          },
          onLeaveBack: () => {
            // Create a timeline to run animations in parallel
            const tl = gsap.timeline();

            // Animate document body background
            tl.to(document.body, {
              backgroundColor: section.fromColor,
              duration: 1,
              ease: "power2.inOut"
            }, 0); // Start at time 0

            // If this is the ValuesSection transition (index 1), also animate card backgrounds back in parallel
            const validCards = valuesCardsRef.current.filter(Boolean);
            if (validCards.length > 0) {
              tl.to(validCards, {
                backgroundColor: section.fromColor,
                duration: 1,
                ease: "power2.inOut"
              }, 0); // Start at time 0 (same time as body animation)
            }
          }
        });
      });
    }
  });

  if (!pageData) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const ctaSectionPageData: CTASectionContent = pageData.sections.find(section => section.type === 'call-to-action')?.content as CTASectionContent;
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;

  const branchInfoSectionPageDataRaw: BranchSectionContent = {
    branches: pageData.sections.find(section => section.type === 'branch')?.content || []
  } as BranchSectionContent;

  const branchInfoSectionPageData: BranchSectionContent = {
    branches: (branchInfoSectionPageDataRaw.branches || []).map(branch => ({
      ...branch,
      openingHoursSatToWed: language === 'arabic' ? '8 ص – 10 م' : '8 AM – 10 PM',
      openingHoursThu: language === 'arabic' ? '8 ص – 8 م' : '8 AM – 8 PM',
      openingHoursFri: language === 'arabic' ? '10 ص – 6:30 م' : '10 AM – 6:30 PM',
      branchAddress: language === 'arabic'
        ? 'لومير سنتر، حي حطين\nRRHA8736، الرياض، السعودية'
        : 'Lumière Center, Hittin\nRRHA8736, Riyadh, SA',
      timetableHeading: language === 'arabic' ? "ساعات العمل" : "Opening hours",
      locationHeading: language === 'arabic' ? "تجدوننا هنا" : "Find us here",
      buttonText: language === 'arabic' ? "انضم إلى ريڤيڤ" : "Join our waitlist",
      satWedLabel: language === 'arabic' ? "السبت - الأربعاء" : "Sat – Wed",
      thursdayLabel: language === 'arabic' ? "الخميس" : "Thu",
      fridayLabel: language === 'arabic' ? "الجمعة" : "Fri",
      googleMapsButtonText: language === 'arabic' ? "توجّه الآن" : "Get directions"
    }))
  };

  const aboutImageSectionPageData: AboutImageSectionContent = {
    images: pageData.sections.find(section => section.type === 'about-image')?.content
  } as AboutImageSectionContent;

  const teamMemberSectionPageData: TeamMemberSectionContent = {
    teamMembers: pageData.sections.find(section => section.type === 'team-member')?.content
  } as TeamMemberSectionContent;

  return (
    <>
      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
      />
      <div className="flex flex-col items-center section-gaps relative">
        <HeroSection
          title={language === 'arabic' ? "عننا" : "About us"}
          paragraph={language === 'arabic'
            ? "في ريڤيڤ، يُكشف الجمال الحقيقي، لا يُغيّر. تعرّف على ما يحفّزنا، وما يُميّزنا، وكيف صُمّمت كلّ تفاصيل ريڤيڤ لدعم رحلتكم نحو الجمال الطبيعي الأصيل."
            : "At Rivive, true beauty is revealed, not altered. Learn what drives us, what sets us apart, and how every detail at Rivive is designed to support your journey toward natural, authentic beauty."
          }
          imageSrc={isLg
            ? "https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Hero_ad923d995f.webp"
            : "https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Hero_Tablet_2_bf4383f18d.webp"
          }
          imageAlt="About hero image"
          language={language}
          titleFont="big"
        />

        <div
          ref={shadowRef}
          className="absolute w-full aspect-3/4 tb:aspect-3/2 z-0 opacity-[8%] tb:opacity-10 pointer-events-none"
          style={{
            top: `0px`,
            transform: 'translateY(-100%)',
            backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            mixBlendMode: 'darken',
          }}
        />

        <TextBenefitsSection
          text={language === 'arabic' ? 'ريڤيڤ هي فرصتكم لتبطئوا إيقاع حياتك، وتستمعوا برعاية مميزة، وتكتشفوا جمالكم الأصيل الذي يُبرز ولا يُعدّل.' : 'Rivive is your invitation to slow down, be truly cared for, and embrace beauty as something to be revealed, not altered.'}
          showLogo={false}
          showShadow={false}
          language={language}
          benefits={language === 'arabic' ? [
            {
              title: 'ملاذ للتجديد',
              content: 'تعمل مساحتنا الهادئة والمستوحاة من الطبيعة على تعزيز راحتكم ودعم كل خطوة من خطوات تحولكم.'
            },
            {
              title: 'عناية شخصية',
              content: 'تركز بروتوكولاتنا على خطط العلاج الفردية المدعومة بالتحليل المتقدم، مما يضمن أن تكون رحلتكم فريدة مثل جمالكم.'
            },
            {
              title: 'تكنولوجيا حصرية',
              content: 'باستخدام تقنيات لن تجدوها في أي مكان آخر داخل المملكة العربية السعودية، نقدم لكم إجراءات فعّالة وغير جراحية مصممة خصيصًا لتلبية احتياجاتكم الفردية.'
            }
          ] : [
            {
              title: 'A sanctuary for renewal',
              content: 'Our calming, nature-inspired space enhances your comfort and supports every step of your transformation.'
            },
            {
              title: 'Personalized care',
              content: 'Our protocols focus on individualized treatment plans powered by advanced analysis, ensuring that your journey is as unique as your beauty.'
            },
            {
              title: "Exclusive technologies",
              content: "Using technologies you won't find anywhere else in KSA, we deliver effective, non-invasive procedures tailored to your specific needs."
            }
          ]}
        />

        <SummarySection
          text={language === 'arabic' ? "طوتك إلى عالم من الرعاية الحقيقية، حيث يتجلى جمالك الطبيعي" : "Step into a world of genuine care meant to reveal your natural beauty."}
          buttonText={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
          imageAlt="Summary image"
          imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Differentiator_f4d438b826.webp"
          language={language}
          onInitialCenterPosition={(pos) => {
            summaryCenterRef.current = pos;
            animateBg();
          }}
          onSectionRefReady={(ref: React.RefObject<HTMLDivElement | null>) => {
            sectionRef.current = ref.current;
            debug.log('background sectionRef.current', sectionRef.current);

            if (sectionRef.current) {
              // Get the scroll position
              const scrollY = window.scrollY;

              const sectionRect = sectionRef.current.getBoundingClientRect();
              const absoluteBottom = sectionRect.bottom + scrollY;

              // Directly update the shadow element's position
              if (shadowRef.current) {
                shadowRef.current.style.top = `${absoluteBottom}px`;
              }
            }

          }}
        />

        <ImageSwiperSection
          aboutImageSectionPageData={aboutImageSectionPageData}
          language={language}
          onInitialPosition={(pos) => {
            imageSwiperPositionRef.current = pos;
            animateBg();
          }}
        />

        <MissionSection
          headlineText={language === 'arabic' ? "نحتفل بجمالك الحقيقي ونرعى رفاهيتك بكل اهتمام." : "Celebrating authentic beauty and wellbeing"}
          subheadingText={language === 'arabic' ? "ما الذي يدفعنا" : "WHAT DRIVES US"}
          paragraphText={language === 'arabic' ? "نقود عملائنا في رحلاتٍ ثورية، حيث نحتفي بالجمال الأصيل، ونُعطي الأولوية للرفاهية على الحلول السريعة.\n\nكما ندعم رؤية السعودية 2030 من خلال ابتكاراتنا في مجال تقنيات التجميل، حيث نوفر بدائل تجميلية أكثر صحةً ووعيًا، تتماشى مع رؤية المملكة في الابتكار والعافية والتعبير عن الذات بصدق." : "We lead our customers on transformative journeys where authentic beauty is celebrated and well-being takes priority over quick fixes.\n\nWe also support Saudi Vision 2030 through our Beauty-Tech innovations, as we provide healthier, more conscious beauty alternatives that align with the Kingdom's vision of innovation, wellness, and authentic self-expression."}
          imageAlt="Mission image"
          imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/About_Mission_4dfe85bf10.webp"
          language={language}
        />

        <ValuesSection
          subheadingText={language === 'arabic' ? "قيمنا الأساسية" : "OUR CORE VALUES"}
          paragraphText={language === 'arabic' ? "تحدد هذه القيم الأساسية ثقافتنا ونهجنا، مما يضمن أن كل زيارة إلى ريڤيڤ تعكس التزامنا بالجمال الأصيل والرعاية الاستثنائية والشخصية." : "These core values define our culture and approach, ensuring that every visit to Rivive reflects our dedication to authentic beauty and exceptional, personalized care."}
          language={language}
          values={language === 'arabic' ? [
            {
              heading: "التقدير الأصيل",
              paragraph: "نقدّر جمالكم الطبيعي باعتباره الفخامة المطلقة، ونحتفل بتميزكم ونرشدكم إلى التحولات التي تكشف عن إشراقتكم الحقيقية دون تغيير جوهركم."
            },
            {
              heading: "مدعوم بالابتكار",
              paragraph: "نحن رواد التقنيات غير الجراحية التي تجمع بين التقدم العلمي والمبادئ الطبيعية، ونتحدى معايير الصناعة باستمرار لتقديم نتائج متفوقة دون أي تنازل."
            },
            {
              heading: "رعاية وضيافة",
              paragraph: "نبني علاقات لا تعتمد فقط على المعاملات، بل نقدم الدفء والرعاية التي تحول خدمتنا إلى ملاذ تشعر فيه بالتقدير الحقيقي."
            },
            {
              heading: "الرفاهية الشاملة",
              paragraph: "نتعامل مع الجمال باعتباره جزءًا لا يتجزأ من الرفاهية، ونوازن بين النتائج الجمالية الفورية والفوائد الصحية الدائمة، ونخلق تجارب يتعايش فيها الهدوء والتحول."
            },
            {
              heading: "دقة مُخصصة",
              paragraph: "نصمم رحلات مخصصة باستخدام رؤى تعتمد على البيانات وإرشادات الخبراء، مع إدراكنا أن الفخامة الحقيقية تكمن في الرعاية المصممة خصيصًا لتلبية احتياجاتك وتطلعاتك الفريدة."
            }
          ] : [
            {
              heading: "Authentic Appreciation",
              paragraph: "We honor your natural beauty as the ultimate luxury, celebrating your uniqueness and guiding transformations that reveal your true radiance without altering your essence."
            },
            {
              heading: "Empowered by Innovation",
              paragraph: "We pioneer non-invasive technologies that merge scientific advancement with natural principles, continuously challenging industry norms to deliver superior results without compromise."
            },
            {
              heading: "Nurturing Hospitality",
              paragraph: "We build relationships based on more than just transactions, providing the warmth and care that transform our service into a sanctuary where you feel truly valued."
            },
            {
              heading: "Holistic Wellbeing",
              paragraph: "We approach beauty as inseparable from wellbeing, balancing immediate aesthetic results with lasting health benefits, and creating experiences where tranquility and transformation coexist."
            },
            {
              heading: "Personalized Precision",
              paragraph: "We craft individualized journeys using data-driven insights and expert guidance, recognizing that true luxury lies in care tailored exclusively to your unique needs and aspirations."
            }
          ]}
          onCardsRefsReady={(refs) => {
            valuesCardsRef.current = refs;
          }}
        />

        <TeamSection
          teamMemberSectionPageData={teamMemberSectionPageData}
          headlineText={language === 'arabic' ? "خبراء عالميون مخصصون لك" : "Global experts dedicated to you"}
          subheadingText={language === 'arabic' ? "تعرفوا على الفريق" : "MEET THE TEAM"}
          paragraphText={language === 'arabic' ? "خبراؤنا العالميون يجمعون بين الإتقان الفني والضيافة الحانية لإبراز جمالك الأصيل—كل ذلك من خلال تجربة شخصية، سرية، وتحويلية" : "Our global experts blend technical mastery with warm hospitality to reveal your authentic beauty — all through a personal, discreet, and transformative experience."}
          language={language}
          onInitialPosition={(pos) => {
            teamPositionRef.current = pos;
            animateBg();
          }}
        />

        <PartnersSection
          language={language}
          sectionSubheading={language === 'arabic' ? "شركاء نثق بهم" : "TRUSTED PARTNERS"}
          sectionHeading={language === 'arabic' ? "لا تصنع الرحلات الجمالية المميزة منفردة, بل تدعمها شراكات موثوقة بعناية فائقه." : "Exceptional beauty journeys are never built alone, but supported by carefully chosen partnerships."}
          partners={[
            { partnerSubheading: "TAMARA", partnerImageSrc: "/Tamara-Business-Partner-Logo.svg", partnerImageSrcMobile: "/Tamara-Business-Partner-Logo-Mobile.svg", partnerImageAlt: "Tamara logo", partnerDescription: language === 'arabic' ? "توفر تمارا لعملاء ريڤيڤ حلول دفع منظمة وموثوقة, لتجربة انضمام سهله بكل ثقه." : "Tamara supports Rivive clients with trusted, regulated payment solutions that make starting memberships seamless and confident." },
            { partnerSubheading: "FAST FIT", partnerImageSrc: "/FastFit-Business-Partner-Logo.svg", partnerImageSrcMobile: "/FastFit-Business-Partner-Logo-Mobile.svg", partnerImageAlt: "Fast Fit logo", partnerDescription: language === 'arabic' ? "يتعاون Fast Fit و Rivive معًا لدعم أسلوب حياة حديث يركز على العافية، من خلال رعاية يقودها خبراء، مع التركيز على التعافي والتوازن." : "Fast Fit and Rivive come together to support a modern wellbeing lifestyle through expert-led care, recovery, and balance." },
          ]}
        />

        <LocationSection
          branchInfoSectionPageData={branchInfoSectionPageData}
          language={language}
          onInitialPosition={(pos) => {
            locationPositionRef.current = pos;
            animateBg();
          }}
          subheadingText={language === 'arabic' ? "موقعنا" : "OUR LOCATION"}
          headingText={language === 'arabic' ? "زوروا واحتنا في قلب الرياض" : "Visit our oasis in the heart of Riyadh"}
        />

        <FooterWrapper
          brandInfoSectionPageData={brandInfoSectionPageData}
          ctaSectionPageData={ctaSectionPageData}
          language={language}
          setLanguage={setLanguage}
        />
      </div>
    </>
  );
}
